import { getAnswer, saveAnswer } from '@/utils/onboardingStorage'
import MoneyBackGuarantee from '@app-studio-web/ui/components/MoneyBackGuarantee'
import Plans from '@app-studio-web/ui/components/Plans'
import { GetPricesData } from '@app-studio-web/ui/loaders/getPricesLoader'
import ErrorPage from '@app-studio-web/ui/pages/ErrorPage'
import LoadingPage from '@app-studio-web/ui/pages/LoadingPage'
import { Plan } from '@app-studio-web/ui/types'
import { Suspense, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Await, useLoaderData } from 'react-router-dom'

function PlansBlock({
  getPricesData,
  isSubmitting,
  selectedPlan,
  setSelectedPlan,
  setIsSubmitting,
}: {
  getPricesData: GetPricesData
  isSubmitting: boolean
  selectedPlan: Plan
  setSelectedPlan: (plan: Plan) => void
  setIsSubmitting: (v: boolean) => void
}) {
  return (
    <div className="mx-auto w-full max-w-[380px] md:max-w-[480px]">
      <Plans
        countdownUntil={0}
        currency={getPricesData.currency}
        firstIntroPrice={getPricesData.firstIntroPrice}
        firstPrice={getPricesData.firstPrice}
        isSubmitting={isSubmitting}
        onPlanChange={setSelectedPlan}
        secondIntroPrice={getPricesData.secondIntroPrice}
        secondPrice={getPricesData.secondPrice}
        selectedPlan={selectedPlan}
        setIsSubmitting={setIsSubmitting}
      />
    </div>
  )
}

function getRiskLevel(score: number) {
  if (score < 20) return { label: 'Minimal', color: '#3b82f6' }
  if (score < 40) return { label: 'Low', color: '#22c55e' }
  if (score < 60) return { label: 'Moderate', color: '#e8934a' }
  if (score < 80) return { label: 'High', color: '#f97316' }
  return { label: 'Critical', color: '#ef4444' }
}

function clamp(n: number) {
  return Math.max(0, Math.min(100, Math.round(n)))
}

function generateSubScores(score: number) {
  return {
    taskExposure: clamp(score + ((score * 7) % 13) - 6),
    marketRisk: clamp(score + ((score * 11) % 17) + 5),
    adaptability: clamp(score - ((score * 5) % 11) + 3),
    positioning: clamp(Math.round(score * 0.06 + ((score * 3) % 7))),
  }
}

function formatTaskLabel(value: string): string {
  return value
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

function generateTaskScores(tasks: string[], mainScore: number) {
  return tasks.slice(0, 5).map((task, i) => {
    const hash = task.split('').reduce((a, c) => a + c.charCodeAt(0), 0)
    const score = clamp(mainScore + ((hash * (i + 1)) % 30) - 12)
    const trend =
      score >= 60
        ? { label: 'RISING', color: '#e8934a' }
        : score >= 35
          ? { label: 'EMERGING', color: '#3b82f6' }
          : { label: 'STABLE', color: '#22c55e' }
    return { task, label: formatTaskLabel(task), score, trend }
  })
}

function ScoreDimensionCard({
  label,
  score,
  description,
}: {
  label: string
  score: number
  description: string
}) {
  const { t } = useTranslation()
  const color =
    score >= 70
      ? '#ef4444'
      : score >= 50
        ? '#e8934a'
        : score >= 30
          ? '#f59e0b'
          : '#22c55e'

  return (
    <div className="border-border rounded-xl border bg-white p-5 text-left">
      <div className="flex items-start justify-between">
        <p className="text-muted text-[0.65rem] font-semibold tracking-[1.5px] uppercase">
          {label}
        </p>
        <span
          className="font-serif text-3xl leading-none font-normal"
          style={{ color }}
        >
          {score}
        </span>
      </div>
      <p className="text-muted mt-2 text-[0.8rem] leading-relaxed">
        {description}
      </p>
      <p className="text-accent mt-2 text-[0.75rem] font-medium">
        {t('pages.checkout.sales.readFullAnalysis', {
          defaultValue: 'Read full analysis',
        })}
      </p>
    </div>
  )
}

export default function PlansPage() {
  const { t } = useTranslation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<Plan>('first')

  const profile = useMemo(() => {
    const role =
      getAnswer('role')?.answer ||
      t('pages.checkout.defaultRole', { defaultValue: 'your role' })
    const field = getAnswer('field')?.answer || ''
    const industry = getAnswer('industry')?.answer || ''
    const level = getAnswer('level')?.answer || ''
    const experience = getAnswer('experience')?.answer || ''
    const companySize = getAnswer('company-size')?.answer || ''
    const proudSkill = getAnswer('proud-skill')?.answer || ''
    const aiImpact = getAnswer('ai-impact')?.answer || ''
    const dailyTasksRaw = getAnswer('daily-tasks')?.answer || ''
    const dailyTasks = dailyTasksRaw.split(', ').filter(Boolean)
    const location = getAnswer('location')?.answer || ''
    const aiReaction = getAnswer('ai-reaction')?.answer || ''
    const careerPriority = getAnswer('career-priority')?.answer || ''
    const aiToolsRaw = getAnswer('ai-tools')?.answer || ''
    let aiToolNames: string[] = []
    try {
      const parsed = JSON.parse(aiToolsRaw)
      aiToolNames = Object.keys(parsed).filter((k) => k !== 'None of these')
    } catch {
      aiToolNames = aiToolsRaw.split(', ').filter(Boolean)
    }
    const aiFrequency = getAnswer('ai-frequency')?.answer || ''
    const writingFrequencyRaw = getAnswer('writing-frequency')?.answer || ''
    const defensiveTraits = writingFrequencyRaw.split(', ').filter(Boolean)
    const jobSecurityRaw = getAnswer('job-security')?.answer || ''
    const jobSignals = jobSecurityRaw.split(', ').filter(Boolean)
    const education = getAnswer('education')?.answer || ''
    const salaryRange = getAnswer('salary-range')?.answer || ''
    const summary = getAnswer('summary')?.answer || ''
    const finalWord = getAnswer('final-word')?.answer || ''
    const decisionMaking = getAnswer('decision-making')?.answer || ''
    const concernFeedback = getAnswer('concern-feedback')?.answer || ''
    const scoreRaw = getAnswer('score')?.answer_key || ''
    let scoreNum = parseInt(scoreRaw, 10)
    if (isNaN(scoreNum)) {
      scoreNum = 42 + Math.floor(Math.random() * 40)
      saveAnswer('score', {
        question_key: 'score',
        question: 'AI Exposure Score',
        answer_key: String(scoreNum),
        answer: String(scoreNum),
      })
    }
    const score = scoreNum
    const risk = getRiskLevel(score)
    return {
      role,
      field,
      industry,
      level,
      experience,
      companySize,
      proudSkill,
      aiImpact,
      dailyTasks,
      score,
      risk,
      location,
      aiReaction,
      careerPriority,
      aiToolNames,
      aiFrequency,
      defensiveTraits,
      jobSignals,
      education,
      salaryRange,
      summary,
      finalWord,
      decisionMaking,
      concernFeedback,
    }
  }, [t])

  const subScores = useMemo(
    () => generateSubScores(profile.score),
    [profile.score],
  )

  const taskScores = useMemo(
    () =>
      profile.dailyTasks.length > 0
        ? generateTaskScores(profile.dailyTasks, profile.score)
        : [],
    [profile.score, profile.dailyTasks],
  )

  const data = useLoaderData() as GetPricesData

  if (isSubmitting) return <LoadingPage />

  const scoreDisplay = String(profile.score)

  const profileParts = [
    profile.field,
    profile.level,
    profile.experience,
    profile.location,
  ]
    .filter(Boolean)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
  const profileLine = profileParts.join(' \u00b7 ')

  const risingCount = taskScores.filter(
    (ts) => ts.trend.label === 'RISING',
  ).length
  const topTask =
    taskScores.length > 0
      ? taskScores.reduce((a, b) => (a.score > b.score ? a : b))
      : null

  return (
    <Suspense fallback={<LoadingPage />}>
      <Await errorElement={<ErrorPage />} resolve={data}>
        {(getPricesData: GetPricesData) => (
          <div className="bg-app-bg mx-auto min-h-screen w-full max-w-[480px] md:max-w-[600px] lg:max-w-[680px]">
            <div className="px-6 pt-3 pb-10 md:px-10">
              <div className="flex flex-col items-center text-center">
                {/* ── Report Header ── */}
                <p className="text-accent mt-6 text-[0.65rem] font-semibold tracking-[2.5px] uppercase">
                  {t('pages.checkout.sales.reportHeader', {
                    defaultValue: 'AI Exposure Report',
                  })}
                </p>
                <h1 className="text-primary mt-2 font-serif text-[2rem] leading-tight font-normal capitalize md:text-[2.5rem]">
                  {profile.role}
                </h1>
                {profileLine && (
                  <p className="text-muted mt-1.5 text-[0.85rem]">
                    {'\ud83d\udcbc'} {profileLine}
                  </p>
                )}

                {/* ── Score Circle (progress ring) ── */}
                <div className="relative mx-auto my-8 h-[180px] w-[180px]">
                  <svg
                    className="h-full w-full -rotate-90"
                    viewBox="0 0 180 180"
                  >
                    <circle
                      cx="90"
                      cy="90"
                      r="84"
                      fill="none"
                      stroke="#e4dfd6"
                      strokeWidth="5"
                    />
                    <circle
                      cx="90"
                      cy="90"
                      r="84"
                      fill="none"
                      stroke={profile.risk?.color || '#e8934a'}
                      strokeWidth="5"
                      strokeLinecap="round"
                      strokeDasharray={2 * Math.PI * 84}
                      strokeDashoffset={
                        2 * Math.PI * 84 * (1 - (profile.score ?? 0) / 100)
                      }
                      style={{
                        filter: profile.risk
                          ? `drop-shadow(0 0 8px ${profile.risk.color}40)`
                          : 'none',
                      }}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-primary m-0 block -translate-y-0.5 p-0 font-serif text-6xl leading-none font-normal tabular-nums">
                      {scoreDisplay}
                    </span>
                  </div>
                </div>

                <p
                  className="w-full text-center text-[0.75rem] font-semibold tracking-[2px] uppercase"
                  style={{ color: profile.risk?.color || '#e8934a' }}
                >
                  {profile.risk
                    ? t('pages.checkout.sales.exposureLabel', {
                        defaultValue: '{{label}} Exposure',
                        label: profile.risk.label,
                      }).toUpperCase()
                    : t('pages.checkout.sales.exposureDefault', {
                        defaultValue: 'AI Exposure Score',
                      }).toUpperCase()}
                </p>

                {/* ── Top Plans ── */}
                <div className="mt-8 flex w-full justify-center">
                  <PlansBlock
                    getPricesData={getPricesData}
                    isSubmitting={isSubmitting}
                    selectedPlan={selectedPlan}
                    setSelectedPlan={setSelectedPlan}
                    setIsSubmitting={setIsSubmitting}
                  />
                </div>

                {/* ── Key Insight ── */}
                <div className="border-accent/20 border-l-accent bg-accent/5 mt-8 w-full rounded-xl border border-l-[3px] p-5 text-left">
                  <p className="text-accent text-[0.65rem] font-semibold tracking-[1.5px] uppercase">
                    {t('pages.checkout.sales.keyInsightLabel', {
                      defaultValue: 'Key Insight',
                    })}
                  </p>
                  <p className="text-primary mt-3 text-[0.85rem] leading-relaxed">
                    {profile.proudSkill
                      ? t('pages.checkout.keyInsight', {
                          defaultValue:
                            'Your answers paint a clear picture \u2014 and for a {{role}}, it is significant: {{risingCount}} of your core tasks have rising AI exposure right now. But {{skill}} is where AI still can\u2019t match a {{level}} {{role}}. Redirect toward the parts of your role AI makes more valuable, not less.',
                          role: profile.role.toLowerCase(),
                          risingCount: risingCount || 2,
                          skill: profile.proudSkill.toLowerCase(),
                          level: profile.level.toLowerCase() || 'experienced',
                        })
                      : t('pages.checkout.keyInsightGeneric', {
                          defaultValue:
                            'Your answers reveal a detailed picture of how AI intersects with your daily work. The full report breaks it down task by task \u2014 showing exactly where you\u2019re exposed and where you have defensive advantage.',
                        })}
                  </p>
                </div>

                {/* ── Score Dimension Cards ── */}
                <div className="mt-6 w-full space-y-4">
                  <ScoreDimensionCard
                    label={t('pages.checkout.sales.dimTaskExposure', {
                      defaultValue: 'Task Exposure',
                    })}
                    score={subScores.taskExposure}
                    description={t('pages.checkout.dims.taskExposure', {
                      defaultValue:
                        '{{count}} of your selected tasks have high AI exposure \u2014 led by {{topTask}} at {{topScore}}%. {{risingCount}} tasks are on a rising trajectory, meaning...',
                      count:
                        taskScores.filter((ts) => ts.score >= 50).length || 2,
                      topTask: topTask?.label.toLowerCase() || 'your top task',
                      topScore: topTask?.score || 70,
                      risingCount: risingCount || 2,
                    })}
                  />
                  <ScoreDimensionCard
                    label={t('pages.checkout.sales.dimMarketRisk', {
                      defaultValue: 'Market Risk',
                    })}
                    score={subScores.marketRisk}
                    description={t('pages.checkout.dims.marketRisk', {
                      defaultValue:
                        'Your {{companySize}} company has {{aiImpact}} AI maturity \u2014 this combination creates real urgency. Even without visible warning signs, the...',
                      companySize:
                        profile.companySize.toLowerCase() || 'company',
                      aiImpact: profile.aiImpact.toLowerCase() || 'growing',
                    })}
                  />
                  <ScoreDimensionCard
                    label={t('pages.checkout.sales.dimAdaptability', {
                      defaultValue: 'Adaptability',
                    })}
                    score={subScores.adaptability}
                    description={
                      profile.aiToolNames.length > 0
                        ? t('pages.checkout.dims.adaptabilityPersonalized', {
                            defaultValue:
                              'You\u2019re using {{aiTools}}, which puts you in the middle of the pack for {{role}}s. Your team is "{{teamAdoption}}" \u2014 your report shows how this dynamic affects your...',
                            aiTools: profile.aiToolNames.slice(0, 3).join(', '),
                            role: profile.role.toLowerCase(),
                            teamAdoption:
                              profile.aiImpact.toLowerCase() || 'evolving',
                          })
                        : t('pages.checkout.dims.adaptability', {
                            defaultValue:
                              'You\u2019ve started adopting AI tools, which puts you in the middle of the pack for {{role}}s. Your team environment is...',
                            role: profile.role.toLowerCase(),
                          })
                    }
                  />
                  <ScoreDimensionCard
                    label={t('pages.checkout.sales.dimPositioning', {
                      defaultValue: 'Positioning',
                    })}
                    score={subScores.positioning}
                    description={
                      profile.defensiveTraits.length > 0
                        ? t('pages.checkout.dims.positioningPersonalized', {
                            defaultValue:
                              'You rely on {{traits}} as your differentiators. In a {{industry}} market with {{networkLevel}} network strength, your positioning against AI-fluent peers is...',
                            traits: profile.defensiveTraits
                              .slice(0, 2)
                              .join(' and ')
                              .toLowerCase(),
                            industry: profile.industry.toLowerCase() || 'your',
                            networkLevel:
                              profile.summary.toLowerCase() || 'moderate',
                          })
                        : t('pages.checkout.dims.positioning', {
                            defaultValue:
                              'Your positioning score is a concern. You haven\u2019t identified strong differentiating factors \u2014 which makes you more...',
                          })
                    }
                  />
                </div>

                {/* ── Task Exposure Map ── */}
                {taskScores.length > 0 && (
                  <div className="mt-10 w-full text-left">
                    <p className="text-muted text-[0.65rem] font-semibold tracking-[2px] uppercase">
                      {t('pages.checkout.sales.taskExposureMap', {
                        defaultValue: 'Task Exposure Map',
                      })}
                    </p>
                    <div className="mt-5 space-y-5">
                      {taskScores.map((ts) => (
                        <div key={ts.task}>
                          <div className="flex items-baseline justify-between">
                            <div>
                              <p className="text-primary text-[0.85rem] font-semibold">
                                {ts.label}
                              </p>
                              <p
                                className="mt-0.5 text-[0.6rem] font-semibold tracking-[1px] uppercase"
                                style={{ color: ts.trend.color }}
                              >
                                {ts.trend.label} {'\u2191'}
                              </p>
                            </div>
                            <span
                              className="text-[0.95rem] font-semibold"
                              style={{ color: ts.trend.color }}
                            >
                              {ts.score}%
                            </span>
                          </div>
                          <div className="bg-border mt-2 h-2 w-full overflow-hidden rounded-full">
                            <div
                              className="h-full rounded-full transition-all duration-500"
                              style={{
                                width: `${ts.score}%`,
                                backgroundColor: ts.trend.color,
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── Here's What We See ── */}
                <div className="border-border mt-14 w-full border-t pt-10 text-left">
                  <h3 className="text-primary font-serif text-[1.6rem] font-normal md:text-[1.8rem]">
                    {t('pages.checkout.sales.heresWhatWeSee', {
                      defaultValue: "Here's what we see.",
                    })}
                  </h3>
                  <div className="bg-accent mt-1 h-[3px] w-8 rounded-full" />

                  <div className="mt-8 space-y-8">
                    <div className="border-l-accent/40 border-l-[3px] pl-5">
                      <p className="text-primary text-[0.9rem] leading-relaxed">
                        {t('pages.checkout.sales.hwwsP1', {
                          defaultValue:
                            "You're {{experience}} {{role}} that the decisions you make in the next 12 months will compound for a decade. The fact that you're here, looking at this number, puts you ahead of 90% of people at your level who are still pretending AI won't affect them.",
                          experience: profile.experience
                            ? `${profile.experience.toLowerCase()} into`
                            : 'early enough in',
                          role: profile.role,
                        })}
                      </p>
                    </div>

                    {(profile.finalWord ||
                      profile.aiFrequency ||
                      profile.aiReaction) && (
                      <div className="border-l-accent/40 border-l-[3px] pl-5">
                        <p className="text-primary text-[0.9rem] leading-relaxed">
                          {profile.finalWord &&
                            t('pages.checkout.sales.hwwsFinalWord', {
                              defaultValue:
                                "You said you feel {{word}} about AI and your career. That's not weakness — that's signal. The people who should be worried are the ones who aren't. ",
                              word: profile.finalWord.toLowerCase(),
                            })}
                          {profile.aiFrequency &&
                            (profile.aiReaction
                              ? t('pages.checkout.sales.hwwsAiFrequency', {
                                  defaultValue:
                                    'You told us that working with AI makes you feel {{feeling}} — and when you first heard "AI is coming for white-collar jobs," your honest reaction was that you\'re {{reaction}}. ',
                                  feeling: profile.aiFrequency.toLowerCase(),
                                  reaction: profile.aiReaction.toLowerCase(),
                                })
                              : t(
                                  'pages.checkout.sales.hwwsAiFrequencySimple',
                                  {
                                    defaultValue:
                                      'You told us that working with AI makes you feel {{feeling}}. ',
                                    feeling: profile.aiFrequency.toLowerCase(),
                                  },
                                ))}
                          {t('pages.checkout.sales.hwwsPlan', {
                            defaultValue:
                              "We're not here to make that feeling go away. We're here to turn it into a plan.",
                          })}
                        </p>
                      </div>
                    )}

                    {taskScores.length > 0 && (
                      <div className="border-l-accent/40 border-l-[3px] pl-5">
                        <p className="text-primary text-[0.9rem] leading-relaxed">
                          {t('pages.checkout.sales.hwwsTasks', {
                            defaultValue:
                              "Of the {{count}} tasks that make up your working life, {{atRisk}} are directly in the path of AI automation. The one you spend the most time on — {{topTask}} — has {{topScore}}% AI capability coverage. And it's accelerating.",
                            count: profile.dailyTasks.length,
                            atRisk: taskScores.filter((ts) => ts.score >= 50)
                              .length,
                            topTask: topTask?.label,
                            topScore: topTask?.score,
                          })}
                        </p>
                      </div>
                    )}

                    <div className="border-l-accent/40 border-l-[3px] pl-5">
                      <p className="text-primary text-[0.9rem] leading-relaxed">
                        {profile.defensiveTraits.length > 0
                          ? t('pages.checkout.sales.hwwsDefensiveTraits', {
                              defaultValue:
                                "You said {{traits}} make you hard to replace. That's real — and your report quantifies exactly how much protection that gives you. But positioning is the single biggest lever you have, and building it now, while the market is still reshuffling, is the smartest move you can make.",
                              traits: profile.defensiveTraits
                                .slice(0, 2)
                                .join(' and ')
                                .toLowerCase(),
                            })
                          : t('pages.checkout.sales.hwwsNoTraits', {
                              defaultValue:
                                "When we asked what makes you hard to replace, you paused. That's honest — and it's the clearest opportunity in your entire profile. Positioning is the single biggest lever you have, and building it now, while the market is still reshuffling, is the smartest move you can make.",
                            })}
                      </p>
                    </div>
                  </div>
                </div>

                {/* ── The Window ── */}
                <div className="border-border mt-14 w-full border-t pt-10 text-left">
                  <h3 className="text-primary font-serif text-[1.6rem] font-normal md:text-[1.8rem]">
                    {t('pages.checkout.sales.windowTitle', {
                      defaultValue: 'The window for {{role}} professionals.',
                      role: profile.role,
                    })}
                  </h3>

                  {/* Timeline graphic */}
                  <div className="bg-primary mt-8 w-full overflow-hidden rounded-xl px-6 py-8 md:px-10">
                    {/* Timeline bar */}
                    <div className="relative mx-auto px-4">
                      <div className="absolute top-[7px] right-4 left-4 h-[3px] bg-[#444]" />
                      <div className="relative grid grid-cols-3">
                        {[
                          {
                            label: t('pages.checkout.sales.timelineNow', {
                              defaultValue: 'Now',
                            }),
                            sub: t('pages.checkout.sales.timelineNowSub', {
                              defaultValue: 'Score: {{score}}',
                              score: scoreDisplay,
                            }),
                            color: '#e8934a',
                          },
                          {
                            label: t('pages.checkout.sales.timelineMid', {
                              defaultValue: '12\u201318 Months',
                            }),
                            sub: t('pages.checkout.sales.timelineMidSub', {
                              defaultValue: 'Optimal window',
                            }),
                            color: '#c9a84c',
                          },
                          {
                            label: t('pages.checkout.sales.timelineLate', {
                              defaultValue: '3+ Years',
                            }),
                            sub: t('pages.checkout.sales.timelineLateSub', {
                              defaultValue: 'Late movers pay a premium',
                            }),
                            color: '#c04040',
                          },
                        ].map((pt, i) => (
                          <div
                            key={pt.label}
                            className={`flex flex-col ${i === 0 ? 'items-start' : i === 1 ? 'items-center' : 'items-end'}`}
                          >
                            <div
                              className="z-10 h-[14px] w-[14px] rounded-full"
                              style={{
                                backgroundColor: pt.color,
                                boxShadow: `0 0 10px ${pt.color}60`,
                              }}
                            />
                            <p
                              className="mt-2.5 text-[0.6rem] font-bold tracking-[1.5px] uppercase"
                              style={{ color: pt.color }}
                            >
                              {pt.label}
                            </p>
                            <p className="mt-0.5 text-[0.6rem] text-[#999]">
                              {pt.sub}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Role-specific paragraph */}
                  <p className="text-primary mt-8 text-[0.95rem] leading-relaxed">
                    {profile.field
                      ? t('pages.checkout.sales.windowFieldP', {
                          defaultValue:
                            "AI can now handle many of the routine tasks in {{field}}. The {{role}}s thriving are the ones who've moved upstream — into strategic thinking, cross-functional leadership, and nuanced judgment that AI can't replicate.",
                          field: profile.field.toLowerCase(),
                          role: profile.role.toLowerCase(),
                        })
                      : t('pages.checkout.sales.windowGenericP', {
                          defaultValue:
                            "AI is rapidly automating the routine parts of the {{role}} role. The professionals thriving are the ones who've moved upstream — into strategic thinking, leadership, and nuanced judgment that AI can't replicate.",
                          role: profile.role.toLowerCase(),
                        })}
                  </p>

                  {/* Concern-feedback callout */}
                  {profile.concernFeedback && (
                    <div className="border-border mt-8 rounded-xl border bg-[#f5f3ef] p-6">
                      <p className="text-muted text-[0.9rem] leading-relaxed">
                        {(() => {
                          const feedback = profile.concernFeedback.toLowerCase()
                          if (feedback === 'motivated')
                            return t('pages.checkout.sales.feedbackMotivated', {
                              defaultValue:
                                "You said that when a colleague outpaces you with AI, you feel motivated. That's a good instinct. But instinct without a system is just admiration. The question is whether you have a plan to be the person others are watching.",
                            })
                          if (feedback === 'unsettled')
                            return t('pages.checkout.sales.feedbackUnsettled', {
                              defaultValue:
                                "You said that when a colleague outpaces you with AI, you feel unsettled. That discomfort is useful — it means you're paying attention. The question is whether you turn that awareness into action before the gap becomes permanent.",
                            })
                          if (
                            feedback === 'impressed-uneasy' ||
                            feedback === 'impressed & uneasy'
                          )
                            return t('pages.checkout.sales.feedbackImpressed', {
                              defaultValue:
                                'You said that when a colleague outpaces you with AI, you feel impressed but uneasy. That tension is the exact right signal. Admiration plus discomfort is what drives the people who actually adapt, rather than just watch.',
                            })
                          if (feedback === 'frustrated')
                            return t(
                              'pages.checkout.sales.feedbackFrustrated',
                              {
                                defaultValue:
                                  "You said that when a colleague outpaces you with AI, you feel frustrated. That's honest. Frustration means you know you should be doing more. The report turns that energy into a concrete, personalized plan.",
                              },
                            )
                          return t('pages.checkout.sales.feedbackNotHappened', {
                            defaultValue:
                              "You said this hasn't happened to you yet. That might change soon — and when it does, you want to be the person others are trying to keep up with, not the other way around.",
                          })
                        })()}
                      </p>
                    </div>
                  )}

                  {/* Urgency italic */}
                  <p className="text-accent/80 mt-8 text-[0.9rem] leading-relaxed italic">
                    {t('pages.checkout.sales.urgency', {
                      defaultValue:
                        "Every month you wait, {{count}} of your daily tasks become easier for AI to handle — and easier for your employer to automate. Your score doesn't stay at {{score}}. It moves.",
                      count:
                        taskScores.filter((ts) => ts.score >= 50).length || 2,
                      score: scoreDisplay,
                    })}
                  </p>
                </div>

                {/* ── Why Now ── */}
                <div className="border-border mt-14 w-full border-t pt-10 text-left">
                  <p className="text-accent text-[0.7rem] font-semibold tracking-[2px] uppercase">
                    {t('pages.checkout.sales.whyNowLabel', {
                      defaultValue: 'Why This Matters Now',
                    })}
                  </p>
                  <h3 className="text-primary mt-2 font-serif text-2xl font-normal">
                    {t('pages.checkout.sales.whyNowTitle', {
                      defaultValue:
                        'The people who act first have the advantage.',
                    })}
                  </h3>
                  <p className="text-muted mt-3 text-[0.85rem] leading-relaxed">
                    {profile.education
                      ? t('pages.checkout.sales.whyNowP1Personalized', {
                          defaultValue:
                            'You said you\u2019re "{{confidence}}" that your current skills will be enough in 3 years. AI capabilities are advancing every quarter. The gap between those who understand their exposure and those who don\u2019t is widening \u2014 and your report quantifies exactly where you stand.',
                          confidence: profile.education.toLowerCase(),
                        })
                      : t('pages.checkout.sales.whyNowP1', {
                          defaultValue:
                            'AI capabilities are advancing every quarter. The gap between those who understand their exposure and those who don\u2019t is widening. Your colleagues who figure this out six months before you will have six months of strategic advantage.',
                        })}
                  </p>
                  {profile.aiImpact && (
                    <p className="text-muted mt-3 text-[0.85rem] leading-relaxed">
                      {t('pages.checkout.sales.whyNowPersonalized', {
                        defaultValue:
                          'You told us your company is "{{aiImpact}}" when it comes to AI. Your report factors this into every recommendation \u2014 because your timeline depends on how fast your organization is moving.',
                        aiImpact: profile.aiImpact,
                      })}
                    </p>
                  )}
                  {profile.jobSignals.length > 0 &&
                    !profile.jobSignals.includes('none') && (
                      <p className="text-muted mt-3 text-[0.85rem] leading-relaxed">
                        {t('pages.checkout.sales.whyNowSignals', {
                          defaultValue:
                            'You\u2019ve already noticed warning signals at your company \u2014 {{signalCount}} of them. These aren\u2019t coincidences. Your report maps exactly how these signals connect to your personal exposure and what to do before they escalate.',
                          signalCount: profile.jobSignals.length,
                        })}
                      </p>
                    )}
                  <p className="text-muted mt-3 text-[0.85rem] leading-relaxed">
                    {profile.salaryRange
                      ? t('pages.checkout.sales.whyNowP2Personalized', {
                          defaultValue:
                            'Your biggest worry is "{{worry}}." This isn\u2019t about fear \u2014 it\u2019s about having the information to prevent exactly that. The same way you wouldn\u2019t invest without understanding the risks, you shouldn\u2019t navigate your career without understanding your AI exposure.',
                          worry: profile.salaryRange.toLowerCase(),
                        })
                      : t('pages.checkout.sales.whyNowP2', {
                          defaultValue:
                            'This isn\u2019t about fear. It\u2019s about information. The same way you wouldn\u2019t invest without understanding the risks, you shouldn\u2019t navigate your career without understanding your AI exposure.',
                        })}
                  </p>
                  {profile.finalWord && (
                    <p className="text-muted mt-3 text-[0.85rem] leading-relaxed italic">
                      {t('pages.checkout.sales.whyNowFinalWord', {
                        defaultValue:
                          'You described your feeling about AI and the future in one word: "{{word}}." Your report turns that feeling into a plan.',
                        word: profile.finalWord,
                      })}
                    </p>
                  )}
                </div>

                {/* ── Your Personal AI Career Strategist ── */}
                <div className="border-border mt-14 w-full border-t pt-10 text-left">
                  <h3 className="text-primary font-serif text-[1.6rem] font-normal md:text-[1.8rem]">
                    {t('pages.checkout.sales.strategistTitle', {
                      defaultValue: 'Your personal AI career strategist.',
                    })}
                  </h3>
                  <p className="text-primary mt-3 text-[0.95rem] leading-relaxed">
                    {t('pages.checkout.sales.strategistSubtitle', {
                      defaultValue:
                        'Not a course. Not generic career coaching. Not "learn AI."',
                    })}
                  </p>
                  <p className="text-primary mt-4 text-[0.9rem] leading-relaxed">
                    {t('pages.checkout.sales.strategistDesc', {
                      defaultValue:
                        "Reposit gives you a dedicated AI coach built on everything you just told us — your role, your tasks, your seniority, your company, your fears, your strengths. Every day, it scans hundreds of AI developments and surfaces only the ones that matter for {{role}} professionals at your level. Then it tells you exactly what to do about them. The goal isn't to survive AI. It's to put you in the top 1% of your field.",
                      role: profile.role.toLowerCase(),
                    })}
                  </p>

                  {/* Daily Intelligence Feed label */}
                  <p className="text-accent mt-10 text-[0.7rem] font-semibold tracking-[2px] uppercase">
                    {t('pages.checkout.sales.dailyFeedLabel', {
                      defaultValue: 'Your Daily Intelligence Feed',
                    })}
                  </p>

                  {/* Mock Daily Brief Card */}
                  <div className="bg-primary mt-4 w-full overflow-hidden rounded-xl px-5 py-6 text-white md:px-8">
                    <div className="flex items-center justify-between">
                      <p className="text-[0.65rem] font-semibold tracking-[1.5px] text-white/60 uppercase">
                        {t('pages.checkout.sales.dailyBrief', {
                          defaultValue: 'Daily Brief',
                        })}{' '}
                        &middot;{' '}
                        {new Date()
                          .toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                          .toUpperCase()}
                      </p>
                      <p className="text-[0.65rem] font-semibold tracking-[1.5px] text-white/60 uppercase">
                        {t('pages.checkout.sales.reposit', {
                          defaultValue: 'Reposit',
                        })}
                      </p>
                    </div>

                    <p className="mt-5 text-[0.85rem] text-white/80">
                      {t('pages.checkout.sales.scoreMoved', {
                        defaultValue: 'Your score moved:',
                      })}{' '}
                      &nbsp;
                      <span className="text-accent rounded bg-white/10 px-1.5 py-0.5 font-bold">
                        {scoreDisplay}
                      </span>
                      <span className="mx-1 text-white/40">&rarr;</span>
                      <span className="text-accent font-bold">
                        {(profile.score ?? 0) + 2} &uarr;
                      </span>
                    </p>

                    <p className="mt-5 text-[0.75rem] text-white/50 italic">
                      {t('pages.checkout.sales.todaysDevelopments', {
                        defaultValue:
                          "Today's developments affecting {{role}} professionals:",
                        role: profile.role.toLowerCase(),
                      })}
                    </p>

                    <div className="mt-4 space-y-4">
                      {[
                        {
                          headline: t('pages.checkout.sales.briefHeadline1', {
                            defaultValue:
                              'AI {{field}} tools generate production-quality output in under 60 seconds',
                            field: profile.field
                              ? profile.field.toLowerCase()
                              : 'productivity',
                          }),
                          sub: t('pages.checkout.sales.briefSub1', {
                            defaultValue:
                              '{{task}} work increasingly automatable',
                            task: topTask ? topTask.label : 'Production',
                          }),
                        },
                        {
                          headline: t('pages.checkout.sales.briefHeadline2', {
                            defaultValue:
                              '{{industry}} teams restructuring: fewer production, more strategy roles',
                            industry: profile.industry || 'Industry',
                          }),
                          sub: t('pages.checkout.sales.briefSub2', {
                            defaultValue:
                              'Upstream skills becoming more valuable',
                          }),
                        },
                        {
                          headline: t('pages.checkout.sales.briefHeadline3', {
                            defaultValue:
                              'Professionals integrating AI seeing 2x more {{priority}} opportunities',
                            priority: profile.careerPriority
                              ? profile.careerPriority.toLowerCase()
                              : 'leadership',
                          }),
                          sub: t('pages.checkout.sales.briefSub3', {
                            defaultValue: 'AI fluency unlocking career growth',
                          }),
                        },
                      ].map((item, i) => (
                        <div
                          key={i}
                          className="border-accent/50 border-l-2 pl-4"
                        >
                          <p className="text-[0.85rem] leading-snug font-semibold text-white/90">
                            {i + 1}. {item.headline}
                          </p>
                          <p className="mt-0.5 text-[0.75rem] text-white/40">
                            {item.sub}
                          </p>
                        </div>
                      ))}
                    </div>

                    <div className="mt-6 border-t border-white/10 pt-5">
                      <p className="text-accent text-[0.6rem] font-bold tracking-[1.5px] uppercase">
                        {t('pages.checkout.sales.whatThisMeans', {
                          defaultValue: 'What This Means For You Specifically',
                        })}
                      </p>
                      <p className="mt-2 text-[0.85rem] leading-relaxed text-white/80">
                        {topTask
                          ? t('pages.checkout.sales.briefMeansWithTask', {
                              defaultValue:
                                "Generate 10 variations of your next {{task}} task using AI. Curate and refine the best ones. You're becoming a creative director, not a {{field}} worker.",
                              task: topTask.label.toLowerCase(),
                              field: profile.field
                                ? profile.field.toLowerCase()
                                : 'production',
                            })
                          : t('pages.checkout.sales.briefMeansGeneric', {
                              defaultValue:
                                'Start using AI to handle the repetitive parts of your workflow. Curate and refine the output. Position yourself as the person who directs AI, not the person AI replaces.',
                            })}
                      </p>
                    </div>

                    <div className="mt-5 border-t border-white/10 pt-5">
                      <p className="text-[0.6rem] font-bold tracking-[1.5px] text-white/40 uppercase">
                        {t('pages.checkout.sales.todaysCoachingAction', {
                          defaultValue: "Today's Coaching Action",
                        })}
                      </p>
                      <p className="mt-2 text-[0.85rem] leading-relaxed text-white/80">
                        {t('pages.checkout.sales.coachingActionText', {
                          defaultValue:
                            "Based on your profile: practice framing AI as leverage, not threat. Take one task from today's brief and show your team how you'd integrate it. This builds your reputation as the person who bridges AI and human judgment.",
                        })}
                      </p>
                    </div>
                  </div>

                  <p className="text-accent/70 mt-6 text-[0.85rem] leading-relaxed italic">
                    {t('pages.checkout.sales.dailyFeedCloser', {
                      defaultValue:
                        'Every morning. Filtered for your exact role, tasks, seniority, and goals. Not generic AI news — your career intelligence.',
                    })}
                  </p>

                  {/* AI Coach mock */}
                  <p className="text-accent mt-14 text-[0.7rem] font-semibold tracking-[2px] uppercase">
                    {t('pages.checkout.sales.aiCoachLabel', {
                      defaultValue: 'Your Hyper-Personalized AI Coach',
                    })}
                  </p>

                  <div className="bg-primary mt-4 w-full overflow-hidden rounded-xl px-5 py-6 text-white md:px-8">
                    <div className="flex items-center justify-between">
                      <p className="text-[0.6rem] font-semibold tracking-[1.5px] text-white/50 uppercase">
                        {t('pages.checkout.sales.aiCoachHeader', {
                          defaultValue:
                            'AI Career Coach · Trained On Your Profile',
                        })}
                      </p>
                      <p className="text-[0.6rem] font-semibold tracking-[1.5px] text-white/50 uppercase">
                        {t('pages.checkout.sales.reposit', {
                          defaultValue: 'Reposit',
                        })}
                      </p>
                    </div>

                    {/* Context banner */}
                    <div className="border-accent/50 mt-5 border-l-2 pl-4">
                      <p className="text-[0.78rem] leading-relaxed text-white/50">
                        {t('pages.checkout.sales.aiCoachContext', {
                          defaultValue:
                            "This coach knows: your role as a {{role}}, your {{experience}} years of experience, your {{taskCount}} core tasks, your AI exposure score of {{score}}, your company's AI maturity, and your career goals.",
                          role: profile.role.toLowerCase(),
                          experience: profile.experience
                            ? profile.experience.toLowerCase()
                            : '7+',
                          taskCount: profile.dailyTasks.length || 3,
                          score: scoreDisplay,
                        })}
                      </p>
                    </div>

                    {/* Coach message */}
                    <div className="mt-6 space-y-4">
                      <p className="text-[0.88rem] leading-relaxed text-white/85">
                        {t('pages.checkout.sales.aiCoachGreeting', {
                          defaultValue:
                            "Good morning. I've analyzed 47 AI developments from the last 24 hours against your profile as a {{level}} {{role}} with {{experience}} of experience in {{industry}}. Three are relevant to you — they're in your daily feed above.",
                          level: profile.level
                            ? `${profile.level.toLowerCase()}-level`
                            : '',
                          role: profile.role,
                          experience: profile.experience
                            ? profile.experience.toLowerCase()
                            : 'years',
                          industry: profile.industry || 'your field',
                        })}
                      </p>

                      <p className="text-[0.88rem] leading-relaxed text-white/85">
                        {t('pages.checkout.sales.aiCoachFocus', {
                          defaultValue: "Here's your coaching focus for today:",
                        })}
                      </p>

                      <p className="text-[0.88rem] leading-relaxed text-white/85">
                        {t('pages.checkout.sales.aiCoachTask', {
                          defaultValue:
                            "Your highest-exposure task — {{task}} — had a significant development yesterday. A new tool is handling {{score}}% of what this task requires. But here's the opportunity: the person in your company who learns to supervise AI on this task becomes the most valuable {{role}} in the building. That's not replacement — that's the path to the top 1%.",
                          task: topTask?.label || 'your top task',
                          score: topTask?.score || 70,
                          role: profile.role.toLowerCase(),
                        })}
                      </p>

                      <p className="text-[0.88rem] leading-relaxed text-white/85">
                        {t('pages.checkout.sales.aiCoachScore', {
                          defaultValue:
                            "Your score is {{score}}. I'll show you how to make that number work for you, not against you.",
                          score: scoreDisplay,
                        })}
                      </p>
                    </div>

                    {/* User question */}
                    <div className="border-accent mt-6 rounded-lg border-l-[3px] bg-white/5 px-5 py-4">
                      <p className="text-accent text-[0.88rem] leading-relaxed">
                        {t('pages.checkout.sales.aiCoachQuestion', {
                          defaultValue:
                            "How do I position myself for this without seeming like I'm trying to replace my own role?",
                        })}
                      </p>
                    </div>

                    {/* Coach answer */}
                    <div className="mt-5">
                      <p className="text-[0.88rem] leading-relaxed text-white/85">
                        {t('pages.checkout.sales.aiCoachAnswer', {
                          defaultValue:
                            "Frame it as efficiency, not replacement. Take your highest-exposure task, run it through an AI tool, and present the results alongside your analysis. You're not replacing yourself — you're demonstrating that you can bridge human judgment and AI capability. That's what puts you in the top 1% of {{role}} professionals — the person who leads the transition, not the one who gets transitioned.",
                          role: profile.role.toLowerCase(),
                        })}
                      </p>
                    </div>
                  </div>

                  <p className="text-accent/70 mt-6 text-[0.85rem] leading-relaxed italic">
                    {profile.field
                      ? t('pages.checkout.sales.aiCoachCloser', {
                          defaultValue:
                            'Not a chatbot. A career strategist trained on your exact profile, goals, and daily AI developments in {{field}}. Available anytime.',
                          field: profile.field.toLowerCase(),
                        })
                      : t('pages.checkout.sales.aiCoachCloserGeneric', {
                          defaultValue:
                            'Not a chatbot. A career strategist trained on your exact profile, goals, and daily AI developments. Available anytime.',
                        })}
                  </p>

                  {/* Features list */}
                  <div className="mt-12 space-y-8">
                    {[
                      {
                        title: t('pages.checkout.sales.feat1Title', {
                          defaultValue: 'Daily AI Intelligence Feed',
                        }),
                        desc: t('pages.checkout.sales.feat1Desc', {
                          defaultValue:
                            'Every morning: the AI developments that matter for {{role}} professionals — filtered from hundreds of sources, explained in context, with your specific action items.',
                          role: profile.role.toLowerCase(),
                        }),
                      },
                      {
                        title: t('pages.checkout.sales.feat2Title', {
                          defaultValue: 'Hyper-Personalized Coaching',
                        }),
                        desc: t('pages.checkout.sales.feat2Desc', {
                          defaultValue:
                            'An AI coach trained on your exact profile — your tasks, your seniority, your company, your score. Not generic advice. Specific moves to put you in the top 1%.',
                        }),
                      },
                      {
                        title: t('pages.checkout.sales.feat3Title', {
                          defaultValue: 'Live Score Monitoring',
                        }),
                        desc: t('pages.checkout.sales.feat3Desc', {
                          defaultValue:
                            'Your exposure score updates as AI capabilities shift. See exactly which of your tasks are becoming more or less exposed — before your employer does.',
                        }),
                      },
                      {
                        title: t('pages.checkout.sales.feat4Title', {
                          defaultValue: '90-Day Repositioning Roadmap',
                        }),
                        desc: t('pages.checkout.sales.feat4Desc', {
                          defaultValue:
                            'A week-by-week plan built for your specific role, tasks, and goals. Updated dynamically as the market moves.',
                        }),
                      },
                      {
                        title: t('pages.checkout.sales.feat5Title', {
                          defaultValue: '{{field}} Playbooks',
                          field: profile.field || 'Industry',
                        }),
                        desc: t('pages.checkout.sales.feat5Desc', {
                          defaultValue:
                            'Field-specific tactics, tools, and positioning strategies updated as new AI capabilities emerge in {{industry}}.',
                          industry: profile.industry
                            ? profile.industry.toLowerCase()
                            : 'your industry',
                        }),
                      },
                      {
                        title: t('pages.checkout.sales.feat6Title', {
                          defaultValue: 'Career Intelligence Network',
                        }),
                        desc: t('pages.checkout.sales.feat6Desc', {
                          defaultValue:
                            "See how professionals at your level are repositioning in real time. Learn from what's working for people with similar profiles.",
                        }),
                      },
                    ].map((f, i) => (
                      <div key={i} className="flex gap-4">
                        <span className="text-accent mt-0.5 text-[0.9rem]">
                          &rarr;
                        </span>
                        <div>
                          <h4 className="text-primary text-[0.95rem] font-bold">
                            {f.title}
                          </h4>
                          <p className="text-muted mt-1 text-[0.85rem] leading-relaxed">
                            {f.desc}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ── Common Questions ── */}
                <div className="border-border mt-14 w-full border-t pt-10 text-left">
                  <p className="text-accent text-[0.7rem] font-semibold tracking-[2px] uppercase">
                    {t('pages.checkout.sales.faqLabel', {
                      defaultValue: 'Common Questions',
                    })}
                  </p>

                  <div className="divide-border mt-6 divide-y">
                    {[
                      {
                        q: t('pages.checkout.sales.faq1Q', {
                          defaultValue:
                            "Can't I just learn this stuff for free on YouTube?",
                        }),
                        a: t('pages.checkout.sales.faq1A', {
                          defaultValue:
                            "You can learn about AI in general. But no YouTube video knows your exact role, your tasks, your seniority, your company's AI maturity, or your exposure score. Generic content gives you awareness. Reposit gives you a personalized strategy built on data you just provided.",
                        }),
                      },
                      {
                        q: t('pages.checkout.sales.faq2Q', {
                          defaultValue:
                            'How is this different from career coaching?',
                        }),
                        a: t('pages.checkout.sales.faq2A', {
                          defaultValue:
                            "Career coaches give advice based on conversations. Reposit gives intelligence based on data — your specific task exposure, real-time AI capability shifts, and peer benchmarks. It's not a person guessing what might help. It's a system that knows exactly where you're exposed and what to do about it.",
                        }),
                      },
                      {
                        q: t('pages.checkout.sales.faq3Q', {
                          defaultValue:
                            "What if AI really won't affect my role?",
                        }),
                        a: t('pages.checkout.sales.faq3A', {
                          defaultValue:
                            "Your score of {{score}} suggests it already is. But even if you're right, knowing your exposure with certainty is more valuable than hoping for the best. The professionals who checked and found they were safe still repositioned — because understanding the landscape gave them leverage.",
                          score: scoreDisplay,
                        }),
                      },
                      {
                        q: t('pages.checkout.sales.faq4Q', {
                          defaultValue:
                            "Can't I just wait and see what happens?",
                        }),
                        a: t('pages.checkout.sales.faq4A', {
                          defaultValue:
                            "That's what the majority of professionals are doing right now. The ones who reposition before the restructuring are the ones who define their terms. The ones who wait tend to react from a position of weakness. Your score moves whether you watch it or not.",
                        }),
                      },
                    ].map((faq, i) => (
                      <details key={i} className="group py-5">
                        <summary className="flex cursor-pointer list-none items-center justify-between">
                          <span className="text-primary pr-4 text-[0.95rem] font-medium">
                            {faq.q}
                          </span>
                          <span className="text-muted shrink-0 text-lg transition-transform group-open:rotate-180">
                            &#8744;
                          </span>
                        </summary>
                        <p className="text-muted mt-3 text-[0.85rem] leading-relaxed">
                          {faq.a}
                        </p>
                      </details>
                    ))}
                  </div>
                </div>

                {/* ── Bottom Plans ── */}
                <div className="border-border mt-14 w-full border-t pt-10">
                  <PlansBlock
                    getPricesData={getPricesData}
                    isSubmitting={isSubmitting}
                    selectedPlan={selectedPlan}
                    setSelectedPlan={setSelectedPlan}
                    setIsSubmitting={setIsSubmitting}
                  />
                </div>

                <div className="mt-6 w-full max-w-[380px] md:max-w-[480px]">
                  <MoneyBackGuarantee />
                </div>
              </div>
            </div>
          </div>
        )}
      </Await>
    </Suspense>
  )
}
